// src/pages/admin/layers/LayersList.tsx
import { PlusIcon } from "@heroicons/react/16/solid";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, collectionGroup, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../../config/firebaseConfig";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";

type LayerRow = { id: string; layerNo: string; title: string; short: string };

export default function LayersList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<LayerRow[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const { user } = useSelector((s: RootState) => s.auth);


  useEffect(() => {
    async function fetchLayers() {
      // 1️⃣ Load layers
      const dataCol = collection(db, "layers", "meta", "data");
      const q = query(dataCol, orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
  
      const layerList = snap.docs.map(d => ({
        id: d.id,
        layerNo: d.data().layerNo,
        title: d.data().title,
        short: d.data().short || ""
      }));
  
      // 2️⃣ Load ALL published templates (no index needed)
      const versionSnap = await getDocs(collectionGroup(db, "versions"));
      const publishedDocs = versionSnap.docs.filter(d => d.id === "published");
  
      // 3️⃣ Build mapping: which template includes which layers
      const templateMap: Record<string, { id: string; title: string }[]> = {};

  
      for (const tplDoc of publishedDocs) {
        const data = tplDoc.data();
        const layerIds: string[] = data.layerIds || [];
  
        // Template ID
        const templateId = tplDoc.ref.parent?.parent?.id;
        if (!templateId) continue;
  
        // Template metadata (title)
        const metaRef = doc(db, "templates", "meta", "data", templateId);
        const metaSnap = await getDoc(metaRef);
        const meta = metaSnap.exists() ? metaSnap.data() : {};
        const templateTitle = meta.title || "Untitled Template";
  
        // Map each layer to this template
        layerIds.forEach(layerId => {
          if (!templateMap[layerId]) templateMap[layerId] = [];
          templateMap[layerId].push({
            id: templateId,
            title: templateTitle
          });
        });

       
      }
  
      // 4️⃣ Attach connectedTemplates to each layer
      const finalList = layerList.map(l => ({
        ...l,
        connectedTemplates: templateMap[l.id] || []
      }));
  
      // 5️⃣ Save to state
      setRows(finalList);
    }
  
    fetchLayers();
  }, []);
  

  const filtered = search
    ? rows.filter(
        (r) =>
          r.layerNo.toLowerCase().includes(search.toLowerCase()) ||
          r.title.toLowerCase().includes(search.toLowerCase())
      )
    : rows;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="mx-8 my-6">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-zinc-800">Layers</h2>
          {(user?.role === "admin" || user?.role === "approver") && (
          <button

            onClick={() => navigate("/admin/layers/new")}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-white hover:bg-brand-600/90"
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            Create layer
          </button>
          )}
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by layer number or title…"
            className="w-full rounded-lg border border-brand-200/60 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-zinc-200">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Layer</th>
                <th className="px-4 py-2 text-left font-medium">Title</th>
                <th className="px-4 py-2 text-left font-medium">Connected With</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {pageRows.map((r:any) => (
                <tr
                  key={r.id}
                  className="hover:bg-zinc-50 cursor-pointer"
                >
                  <td
                    className="px-4 py-3 text-brand-600 underline"
                    onClick={() => navigate(`/admin/layers/preview/${r.id}`)}
                  >
                    {r.layerNo}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 truncate max-w-[30ch]">{r.title}</td>
                  <td className="px-4 py-3 text-zinc-700">
                  {r.connectedTemplates?.length > 0 ? (
  <div className="flex flex-wrap gap-1">
    {r.connectedTemplates.map((tpl: any, index: number) => (
      <span key={tpl.id} className="truncate max-w-[30ch]">
        <span
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/templates/${tpl.id}?ver=published`);
          }}
          className="text-brand-600 hover:underline cursor-pointer "
        >
          {tpl.title}
        </span>

        {/* Add comma except for last item */}
        {index < r.connectedTemplates.length - 1 && <span>, </span>}
      </span>
    ))}
  </div>
) : (
  <span className="text-zinc-400">--</span>
)}

                  </td>
                </tr>
              ))}
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-zinc-400">
                    No results
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-center gap-6 text-sm text-zinc-600">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="disabled:opacity-40"
          >
            ← Previous
          </button>
          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5)
              .map((n) => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={[
                    "rounded px-2 py-1",
                    n === page ? "bg-zinc-900 text-white" : "hover:bg-zinc-100",
                  ].join(" ")}
                >
                  {n}
                </button>
              ))}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="disabled:opacity-40"
          >
            Next →
          </button>
        </div>
    </div>
  );
}
